import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./AuthPage.css";
import { useResetPasswordMutation } from "./services/auth";
import AuthLeftPanel from "./component/AuthLeftPanel";
import { PASSWORD_HINT, validatePasswordClient } from "./utils/passwordPolicy";

const ResetPasswordConfirm = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => navigate("/login"), 3000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const passwordProblems = validatePasswordClient(newPassword);
    if (passwordProblems.length > 0) {
      setError(passwordProblems.join(" "));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await resetPassword({ uid, token, new_password: newPassword }).unwrap();
      setSuccess(true);
    } catch (err) {
      setError(err.data?.error || "An error occurred. Please try again.");
    }
  };

  return (
    <>
      <nav className="auth-nav">
        <Link to="/" className="auth-nav-logo">
          <img
            src="/StratusEdgeFinal_whiteupscaled.png"
            alt="Edge AI"
            className="auth-nav-logo-img"
          />
        </Link>
      </nav>
      <div className="auth-split">
      <AuthLeftPanel variant="login" />
      <div className="auth-split-border" aria-hidden="true" />

      <div className="auth-split-right">
        <div className="auth-form-shell auth-form-animate auth-form-forgot">
          <h2 className="auth-heading">Create new password</h2>
          <p className="auth-subheading">Enter and confirm your new password below.</p>

          {success ? (
            <div className="auth-success-state" role="status">
              <div className="auth-success-icon" aria-hidden="true">✓</div>
              <p className="auth-success-text">Password reset successfully!</p>
              <p className="auth-success-hint">Redirecting to sign in…</p>
            </div>
          ) : (
            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="auth-row">
                <div className="auth-input-container">
                  <label htmlFor="reset-new-password">New Password</label>
                  <input
                    id="reset-new-password"
                    type="password"
                    placeholder="••••••••"
                    className="auth-input auth-input-lg"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    aria-describedby="reset-new-password-hint"
                    required
                  />
                  <p id="reset-new-password-hint" className="auth-success-hint">
                    {PASSWORD_HINT}
                  </p>
                </div>
              </div>

              <div className="auth-row">
                <div className="auth-input-container">
                  <label htmlFor="reset-confirm-password">Confirm Password</label>
                  <input
                    id="reset-confirm-password"
                    type="password"
                    placeholder="••••••••"
                    className="auth-input auth-input-lg"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </div>

              {error && <p className="auth-inline-error">{error}</p>}

              <button type="submit" className="auth-button auth-button-primary" disabled={isLoading}>
                {isLoading ? (
                  <span className="auth-button-loading">
                    <span className="auth-spinner" aria-hidden="true" />
                    Resetting…
                  </span>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}

          <div className="auth-footer">
            <span>Remembered your password?</span>{" "}
            <Link to="/login" className="auth-link-inline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ResetPasswordConfirm;
